import { queryOptions, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import PurchaserClient from "../PurchaserClient";
import type { ThreatListResponse, ThreatType } from "../Purchaser/Threats";

const THREAT_PAGE_SIZE = 100;

export const THREATS_BASE_QUERY_KEY = "securityThreats";

export const ThreatQueryOptions = {
    Threats(threatType?: ThreatType) {
        return {
            queryKey: [THREATS_BASE_QUERY_KEY, threatType ?? "ALL"],
            queryFn: ({ pageParam }: { pageParam?: number }) =>
                PurchaserClient.getThreats({ cursor: pageParam, limit: THREAT_PAGE_SIZE, threatType }),
            initialPageParam: undefined as number | undefined,
            getNextPageParam: (lastPage: ThreatListResponse) => lastPage.nextCursor ?? undefined,
        };
    },
    ThreatDetail(id?: number | null) {
        return queryOptions({
            queryKey: [THREATS_BASE_QUERY_KEY, id],
            queryFn: () => PurchaserClient.getThreatDetail(id as number),
            enabled: !!id,
            staleTime: 5 * 60 * 1000,
        });
    },
};

export const useThreats = (threatType?: ThreatType) => {
    const query = useInfiniteQuery(ThreatQueryOptions.Threats(threatType));

    const threats = useMemo(() => {
        return query.data?.pages.flatMap(page => page.threats) ?? [];
    }, [query.data]);

    return {
        threats,
        ...query,
    };
};

export const useThreatDetail = (id?: number | null) => {
    const query = useQuery(ThreatQueryOptions.ThreatDetail(id));
    const { data: threat, ...rest } = query;

    return {
        threat,
        ...rest,
    };
};
